import { memo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
import classNames from 'classnames';
// Import Store
import { useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
import { selectPartnerSettings } from 'store/slices/partnerSettings';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { SegmentHttpService } from 'services/http';
// Import Components
import { RadioGroup, Icon, Button, BusyLoader, Tooltip } from '@geneui/components';
import SegmentItems from './SegmentItems';
import SegmentReportPreviewModal from 'components/SegmentReportPreviewModal';
import SectionWithTitle from 'components/common/SectionWithTitle';
import SegmentClientsCount from 'components/common/SegmentClientsCount';
// Import Constants
import { SegmentContainerConfig } from './config';
import { l } from 'constants/common';
// Import Enums
import { SegmentClientsCountRequestEnum } from 'enums';

const { defaultColumnsReport, minSegmentsCount, radioGroupOptions } = SegmentContainerConfig;

const SegmentsContainer = ({
    getRowByIndex,
    firstSegment,
    setFirstSegment,
    segments,
    setSegments,
    radioGroupValue,
    setRadioGroupValue,
    getConfig,
    isLoading,
    dropAcceptType,
    resetClientsCountTrigger,
}) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const pageValidation = useAppSelector(selectPageValidation);
    const { CompositeSegmentMaxCount } = useAppSelector(selectPartnerSettings) ?? {};

    const [clientsData, setClientsData] = useState(null);
    const [isVisibleModal, setIsVisibleModal] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const prevRadioGroupValueRef = useRef(radioGroupValue);

    const { getCompositeSegmentPreview } = useCancelableRequests(
        { getCompositeSegmentPreview: SegmentHttpService.getCompositeSegmentPreview },
        { takeLatest: ['getCompositeSegmentPreview'] },
    );

    const isInvalidCompositeOperation =
        !isNil(pageValidation?.Operation?.isValid) && !pageValidation?.Operation?.isValid;

    // Triggers RadioGroup remount (full reset) only when the value transitions from selected (not null) to cleared (null).
    useEffect(() => {
        const previousValue = prevRadioGroupValueRef.current;
        const currentValue = radioGroupValue;

        if (!isNil(previousValue) && isNil(currentValue)) {
            setResetKey((prevKey) => prevKey + 1);
        }

        prevRadioGroupValueRef.current = currentValue;
    }, [radioGroupValue]);

    const isDisabledSegmentContainerActions = () => {
        if (!isNil(firstSegment?.segmentId)) {
            const { Operation, SegmentIds } = getConfig();
            return isNil(Operation) || SegmentIds?.length < minSegmentsCount;
        }
        return true;
    };

    const getSegmentReports = (compositeConfig) => {
        const config = { ...compositeConfig };
        config.Columns = defaultColumnsReport;
        doPostRequest(getCompositeSegmentPreview.request, {
            requestBody: config,
            successCallback: (Data) => setClientsData(Data.Data[0]),
        });
    };

    const handleClickPreview = () => {
        setIsVisibleModal(true);
        const config = { Config: getConfig() };
        getSegmentReports(config);
    };

    const closeModal = () => {
        setIsVisibleModal(false);
        setClientsData(null);
    };

    const getCount = () => {
        // eslint-disable-next-line no-extra-boolean-cast
        let count = !!firstSegment?.enableDropHere ? 0 : 1;
        // for each because can drop enable first not 1 or more fields
        segments.forEach((element) => {
            // eslint-disable-next-line no-extra-boolean-cast
            count += !!element.enableDropHere ? 0 : 1;
        });
        return count;
    };

    const getRadioGroupOptions = () => {
        return radioGroupOptions.map((item) => {
            return {
                label: (
                    <div className={classNames('union-icon', { 'svg-icon': !!item.svgIcon })}>
                        {item.svgIcon ? item.svgIcon : <Icon type={item.icon} />} {t(item.label)}
                    </div>
                ),
                value: item.value,
            };
        });
    };

    return (
        <SectionWithTitle
            title={t(l.CompositeSegmentContainer)}
            hasContentPadding={false}
            contentClassName="segment-container-inner"
        >
            <BusyLoader isBusy={isLoading && isNil(dropAcceptType)} type="spinner" spinnerSize="medium">
                {!isNil(dropAcceptType) && (
                    <>
                        <div className="total-block">
                            <div className="total-calculated-block-inner">
                                <span>
                                    {t(l.Count)} / {t(l.Total)}
                                </span>
                                <SegmentClientsCount
                                    isDisabled={isDisabledSegmentContainerActions()}
                                    config={{
                                        type: SegmentClientsCountRequestEnum.CompositeSegment,
                                        compositeSegmentConfig: getConfig(),
                                    }}
                                    resetClientsCountTrigger={resetClientsCountTrigger}
                                />
                            </div>
                            <Button
                                color="primary"
                                appearance="minimal"
                                icon="bc-icon-view-doc"
                                className="ml-10"
                                disabled={isDisabledSegmentContainerActions()}
                                onClick={handleClickPreview}
                            />
                        </div>
                        <div className="union-block-error">
                            <div
                                className={classNames('union-block composite-segment-union-block', {
                                    'union-block-error-state': isInvalidCompositeOperation,
                                })}
                            >
                                <RadioGroup
                                    key={resetKey}
                                    name="controlled"
                                    options={getRadioGroupOptions()}
                                    type="tab"
                                    value={radioGroupValue}
                                    onChange={(e) => setRadioGroupValue(e.target.value)}
                                />
                                <Tooltip
                                    style={{ marginTop: '5px' }}
                                    text={
                                        (
                                            <div>
                                                <div>
                                                    <span>{t(l.Union)}</span>
                                                    <p>{t(l.UnionTooltipText)}</p>
                                                </div>
                                                <div>
                                                    <span>{t(l.Intersect)}</span>
                                                    <p>{t(l.IntersectTooltipText)}</p>
                                                </div>
                                                <div>
                                                    <span>{t(l.Difference)}</span>
                                                    <p>{t(l.DifferenceTooltipText)}</p>
                                                </div>
                                            </div>
                                        ) as unknown as string
                                    }
                                    position="bottom"
                                >
                                    <Icon type="bc-icon-info" />
                                </Tooltip>
                            </div>
                            <p className="union-block-error-text">
                                {isInvalidCompositeOperation && t(pageValidation?.Operation?.errorText)}
                            </p>
                        </div>

                        <div className="droping-container">
                            <div className="droping-container-inner">
                                <div className="droping-container-info">
                                    {!isEmpty(segments) && !isEmpty(firstSegment) && (
                                        <SegmentItems
                                            getRowByIndex={getRowByIndex}
                                            firstSegment={firstSegment}
                                            setFirstSegment={setFirstSegment}
                                            segments={segments}
                                            setSegments={setSegments}
                                            dropAcceptType={dropAcceptType}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="segment-txt">
                            <span className="segment-count">
                                {getCount()}
                                {'/'}
                                {CompositeSegmentMaxCount}
                            </span>
                            {t(l.Segments)}
                        </div>
                    </>
                )}
                {isVisibleModal && (
                    <SegmentReportPreviewModal
                        isVisible={isVisibleModal}
                        closeHandler={closeModal}
                        clientsData={clientsData}
                    />
                )}
            </BusyLoader>
        </SectionWithTitle>
    );
};

SegmentsContainer.propTypes = {
    getRowByIndex: PropTypes.func.isRequired,
    firstSegment: PropTypes.object.isRequired,
    segments: PropTypes.array.isRequired,
    setFirstSegment: PropTypes.func.isRequired,
    setSegments: PropTypes.func.isRequired,
    radioGroupValue: PropTypes.number,
    setRadioGroupValue: PropTypes.func.isRequired,
    getConfig: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    dropAcceptType: PropTypes.string.isRequired,
    resetClientsCountTrigger: PropTypes.number,
};

export default memo(SegmentsContainer);
